import { memoize } from "@use/decorators";

import {
  ContentElementData,
  ContentElementModel,
} from "@courses-pm2/api/src/ts/models/elements/ContentElementModel";
import { parseSimpleHtml } from "@courses-pm2/api/src/ts/parser/parseSimpleHtml";

const PUNCTUATION_REGEX = /[.,!?]\s*$/;

/**
 * Specifies the desired length of the preview text, given in number of
 * characters.
 */
const DESIRED_PREVIEW_TEXT_LENGTH = 150;

/**
 * Specification of data needed to instantiate a new model.
 */
export type CaptionedContentElementData = ContentElementData & {
  title?: string;
  description?: string;
  caption?: {
    authorOrArtist?: string;
    description?: string;
    label?: string;
    country?: string;
    year?: string;
    creditLine?: string;
    duration?: string;
  };
  displayCaptionAbove?: boolean;
  displayCaptionBelow?: boolean;
};

/**
 * Core class for all content elements describing shared data
 */
export abstract class CaptionedContentElementModel extends ContentElementModel {
  /**
   * Specifies the main title of the content element.
   */
  public readonly title?: string;

  /**
   * Contains an optional description of the content element, which can be
   * used primarily to describe the content of media files.
   */
  public readonly description?: string;

  /**
   * Contains all data needed to render the caption below or above the media
   * element.
   */
  public readonly caption?: {
    /**
     * Specifies the name of the author or artist who created the media.
     */
    authorOrArtist?: string;

    /**
     * Specifies a freetext label that'll be rendered before the rest of the
     * data in the caption.
     */
    label?: string;

    /**
     * Specifies the country of origin related to the media.
     */
    country?: string;

    /**
     * Specifies a credit line that should be displayed in relation to the
     * media, providing a free text input to add extra credit info (on top of
     * the rest of the fields).
     */
    creditLine?: string;

    /**
     * Specifies the duration of media files (applies only to audio and
     * video).
     */
    duration?: string;

    year?: string;
    numberOfPages?: string;
  };

  /**
   * Specifies if the caption (along with all of the extra props) should be
   * rendered above the audio file itself.
   */
  public readonly displayCaptionAbove?: boolean;

  /**
   * Specifies if the caption (along with all of the extra props) should be
   * rendered below the audio file itself.
   */
  public readonly displayCaptionBelow?: boolean;

  /**
   * Apply initial data when a ContentElementModel is being created.
   */
  constructor(initial: CaptionedContentElementData) {
    super(initial);

    this.title = initial.title;
    this.description = initial.description;
    this.caption = initial.caption;
    this.displayCaptionAbove = initial.displayCaptionAbove;
    this.displayCaptionBelow = initial.displayCaptionBelow;
  }

  /**
   * Helper that'll render the title of the element, supporting certain simple
   * html tags.
   */
  @memoize()
  public renderTitle(): React.ReactNode {
    return this.title ? parseSimpleHtml(this.title+ (this.description || this.getCaptionText() ? "," : "")) : [];
  }

  /**
   * Helper that'll render the description of the element, supporting certain
   * simple html tags.
   */
  @memoize()
  public renderDescription(): React.ReactNode {
    return this.description ? parseSimpleHtml(this.description + (this.getCaptionText() ? "," : "")) : [];
  }

  /**
   * Helper that'll render the caption of the element, supporting certain
   * simple html tags.
   */
  @memoize()
  public renderCaption(): React.ReactNode {
    return this.caption ? parseSimpleHtml(this.getCaptionText()) : [];
  }

  /**
   * Generate the combined html content of the caption that's supposed to be
   * rendered for this element.
   */
  @memoize()
  public getCaptionText(): string {
    return this.caption
      ? [
        this.caption.label,
        this.caption.authorOrArtist,
        this.caption.year,
        this.caption.creditLine ? `© ${this.caption.creditLine}` : undefined,
        this.caption.country,
        this.caption.duration,
        this.caption.numberOfPages,
      ].filter((it): it is string => Boolean(it)).map((it, index, arr) => index >= arr.length - 1 || PUNCTUATION_REGEX.test(it) ? it : `${it},`).join(" ")
      : "";
  }

  /**
   * Preview text can only be generated if title and description is present on
   * this element.
   */
  @memoize()
  public getPreviewText(): string | undefined {
    let previewText: string = "";

    // Push title to the preview text
    if (this.title) {
      previewText += this.title;
    }

    if (
      this.description &&
      previewText.length < DESIRED_PREVIEW_TEXT_LENGTH - 3
    ) {
      // Separate description and title
      if (previewText.length) {
        previewText += " - ";
      }

      // Crop at the word in the description closest to the desired
      // preview text length
      const cropAt = this.description.indexOf(
        " ",
        DESIRED_PREVIEW_TEXT_LENGTH - previewText.length
      );

      // Extract the desired amount of text from the description
      const descriptionExtract =
        cropAt > -1 ? this.description.substr(0, cropAt) : this.description;

      // Push to the preview text
      previewText += descriptionExtract;

      // Add ellipsis if we've cropped the description
      if (descriptionExtract.length < this.description.length) {
        previewText += "...";
      }
    }

    // Return the preview!
    return previewText ? previewText : undefined;
  }
}
